import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { IntegrationsNav } from "@/components/feature/IntegrationsNav";
import { isAdmin } from "@/lib/permissions";
import { fmtDateTime } from "@/lib/format";
import { seedShippingSetup } from "@/lib/shipping";
import {
  carrierRegistry,
  carrierMeta,
  type CarrierAccountRow,
  registerCarrierAccount,
  updateCarrierAccount,
  testCarrierConnection,
} from "@/lib/carriers";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const CAPABILITY_LABELS: Record<string, string> = {
  test_connection: "Test connection",
  validate_address: "Address validation",
  get_rates: "Rates",
  create_shipment: "Shipments",
  get_label: "Labels",
  get_tracking: "Tracking",
  cancel_shipment: "Cancel",
  create_return: "Returns",
};

function authBadge(authStatus: string): { status: string; label: string } {
  switch (authStatus) {
    case "connected":
      return { status: "connected", label: "Connected" };
    case "not_configured":
      return { status: "not_configured", label: "Not configured" };
    case "expired":
      return { status: "expired", label: "Expired" };
    case "error":
      return { status: "error", label: "Error" };
    default:
      return { status: "inactive", label: authStatus || "Unknown" };
  }
}

function healthDot(healthStatus: string): { color: string; label: string } {
  switch (healthStatus) {
    case "healthy":
      return { color: "bg-emerald-500", label: "Healthy" };
    case "error":
      return { color: "bg-red-500", label: "Attention required" };
    default:
      return { color: "bg-slate-300", label: "Unknown" };
  }
}

function capabilitiesFor(account: CarrierAccountRow): Record<string, boolean> {
  if (account.capabilities && typeof account.capabilities === "object" && Object.keys(account.capabilities).length > 0) {
    return account.capabilities as Record<string, boolean>;
  }
  return carrierMeta(account.carrier)?.capabilities ?? {};
}

interface AccountForm {
  carrier: string;
  name: string;
  external_account_id: string;
  credential_ref: string;
}

const emptyForm: AccountForm = { carrier: "fedex", name: "", external_account_id: "", credential_ref: "" };

export default function Carriers() {
  const { org, role } = useAuth();
  const admin = isAdmin(role);
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CarrierAccountRow | null>(null);
  const [form, setForm] = useState<AccountForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ accountId: string; ok: boolean; message: string } | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["carrier-accounts", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      await seedShippingSetup().catch(() => undefined);
      const { data: rows } = await supabase
        .from("carrier_accounts")
        .select("*")
        .eq("org_id", org!.id)
        .order("carrier")
        .order("created_at", { ascending: true });
      return (rows ?? []) as CarrierAccountRow[];
    },
  });

  const accounts = data ?? [];

  const openCreate = () => {
    setError("");
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const openEdit = (account: CarrierAccountRow) => {
    setError("");
    setForm({
      carrier: account.carrier,
      name: account.name,
      external_account_id: account.external_account_id ?? "",
      credential_ref: account.credential_ref ?? "",
    });
    setEditing(account);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError("Display name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await updateCarrierAccount({
          accountId: editing.id,
          name: form.name.trim(),
          external_account_id: form.external_account_id.trim() || null,
          credential_ref: form.credential_ref.trim() || null,
        });
        if (!res.ok) setError(res.error ?? "Couldn't update account.");
      } else {
        const res = await registerCarrierAccount({
          carrier: form.carrier,
          name: form.name.trim(),
          external_account_id: form.external_account_id.trim() || null,
          credential_ref: form.credential_ref.trim() || null,
        });
        if (!res.ok) setError(res.error ?? "Couldn't add account.");
      }
      if (!error) {
        setCreateOpen(false);
        setEditing(null);
        queryClient.invalidateQueries({ queryKey: ["carrier-accounts", org?.id] });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save account.");
    }
    setSaving(false);
  };

  const toggleEnabled = async (account: CarrierAccountRow) => {
    setBusyId(account.id);
    setError("");
    try {
      const res = await updateCarrierAccount({ accountId: account.id, enabled: !account.enabled });
      if (!res.ok) setError(res.error ?? "Couldn't update account.");
      queryClient.invalidateQueries({ queryKey: ["carrier-accounts", org?.id] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't update account.");
    }
    setBusyId(null);
  };

  const runTest = async (account: CarrierAccountRow) => {
    setBusyId(account.id);
    setTestResult(null);
    try {
      const res = await testCarrierConnection(account.id);
      setTestResult({
        accountId: account.id,
        ok: res.ok === true,
        message: res.ok
          ? "Connection successful."
          : res.state === "not_configured"
            ? "Not configured — no server-side credentials are set for this account."
            : res.error ?? "Connection failed.",
      });
    } catch (e) {
      setTestResult({
        accountId: account.id,
        ok: false,
        message: e instanceof Error ? e.message : "Connection failed.",
      });
    }
    setBusyId(null);
    queryClient.invalidateQueries({ queryKey: ["carrier-accounts", org?.id] });
  };

  return (
    <div>
      <PageHeader title="Carriers" subtitle="Shipping carrier accounts available for fulfilment and postage">
        {admin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" /> Add carrier account
          </button>
        )}
      </PageHeader>

      {!admin && (
        <div className="mb-4 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500">
          Read-only access — only owners and admins can manage carrier accounts and credentials.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8">
              <LoadingState />
            </div>
          ) : isError ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-sm text-slate-500 mb-4">Couldn't load carrier accounts.</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap"
              >
                Retry
              </button>
            </div>
          ) : accounts.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200">
              <EmptyState
                icon="ri-truck-line"
                title="No carrier accounts"
                description="Add a carrier account to enable shipping. You can connect multiple accounts per carrier."
                action={
                  admin ? (
                    <button
                      onClick={openCreate}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                    >
                      Add carrier account
                    </button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <>
              {error && (
                <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>
              )}
              {carrierRegistry.map((meta) => {
                const group = accounts.filter((a) => a.carrier === meta.carrier);
                if (group.length === 0) return null;
                return (
                  <div key={meta.carrier} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                          <i className={`${meta.icon} text-lg`} />
                        </span>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-800">{meta.label}</h3>
                          <p className="text-xs text-slate-400">{meta.description}</p>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-slate-400 whitespace-nowrap">
                        {group.length} account{group.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {group.map((account) => {
                        const badge = authBadge(account.auth_status);
                        const health = healthDot(account.health_status);
                        const caps = capabilitiesFor(account);
                        const capKeys = Object.keys(caps).filter((k) => caps[k]);
                        return (
                          <div key={account.id} className="px-4 py-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-semibold text-slate-800">{account.name}</p>
                                  <StatusBadge status={badge.status} customLabel={badge.label} />
                                  {!account.enabled && <StatusBadge status="inactive" />}
                                </div>
                                <div className="mt-1.5 space-y-1 text-xs text-slate-500">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-slate-400">Account ID:</span>
                                    <span className="font-mono">{account.external_account_id ?? "—"}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-slate-400">Server secret:</span>
                                    <span className="font-mono">{account.credential_ref ?? "not configured"}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {admin && (
                                  <button
                                    onClick={() => runTest(account)}
                                    disabled={busyId === account.id}
                                    className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1.5"
                                  >
                                    <i className="ri-plug-line" /> {busyId === account.id ? "Testing..." : "Test"}
                                  </button>
                                )}
                                {admin && (
                                  <button
                                    onClick={() => openEdit(account)}
                                    disabled={busyId === account.id}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                                    title="Edit account"
                                  >
                                    <i className="ri-pencil-line" />
                                  </button>
                                )}
                                {admin && (
                                  <button
                                    onClick={() => toggleEnabled(account)}
                                    disabled={busyId === account.id}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"
                                    title={account.enabled ? "Disable" : "Enable"}
                                  >
                                    <i className={account.enabled ? "ri-toggle-fill text-emerald-600" : "ri-toggle-line"} />
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="mt-2.5 flex items-center gap-4 flex-wrap text-xs text-slate-500">
                              <span className="flex items-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full ${health.color}`} />
                                {health.label}
                              </span>
                              {account.last_success_at && (
                                <span>
                                  Last success {fmtDateTime(account.last_success_at)}
                                </span>
                              )}
                              {account.last_error_at && (
                                <span>
                                  Last error {fmtDateTime(account.last_error_at)}
                                </span>
                              )}
                            </div>

                            {account.connection_error && (
                              <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                                {account.connection_error}
                              </p>
                            )}

                            {testResult?.accountId === account.id && (
                              <p
                                className={`mt-2 text-xs rounded-lg px-2.5 py-1.5 border ${
                                  testResult.ok
                                    ? "text-emerald-700 bg-emerald-50 border-emerald-100"
                                    : "text-amber-700 bg-amber-50 border-amber-100"
                                }`}
                              >
                                {testResult.message}
                              </p>
                            )}

                            <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                              {capKeys.length === 0 ? (
                                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">
                                  Manual / offline
                                </span>
                              ) : (
                                capKeys.map((k) => (
                                  <span key={k} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap">
                                    {CAPABILITY_LABELS[k] ?? k}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
        <IntegrationsNav />
      </div>

      <Modal
        open={createOpen || !!editing}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        title={editing ? "Edit carrier account" : "Add carrier account"}
        footer={
          <>
            <button
              onClick={() => {
                setCreateOpen(false);
                setEditing(null);
              }}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {saving ? "Saving..." : editing ? "Save changes" : "Add account"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          {error && (
            <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>
          )}
          <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500 leading-relaxed">
            Credentials (API keys, secrets, OAuth tokens) are stored server-side and never sent to the browser. Provide
            the name of the server-side secret in the field below — the actual value is provisioned in the backend.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!editing && (
              <div className="sm:col-span-2">
                <label className={labelCls}>Carrier</label>
                <select value={form.carrier} onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))} className={inputCls}>
                  {carrierRegistry.map((p) => (
                    <option key={p.carrier} value={p.carrier}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>Display name *</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls}
                placeholder="e.g. FedEx Account 1"
              />
            </div>
            <div>
              <label className={labelCls}>External account ID</label>
              <input
                value={form.external_account_id}
                onChange={(e) => setForm((f) => ({ ...f, external_account_id: e.target.value }))}
                className={inputCls}
                placeholder="e.g. 1234567"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Server-side secret name</label>
              <input
                value={form.credential_ref}
                onChange={(e) => setForm((f) => ({ ...f, credential_ref: e.target.value }))}
                className={inputCls}
                placeholder="e.g. FEDEX_ACCOUNT_1"
              />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}